/**
 * TextButton / TextAnchor — an inline "View all ›" affordance: plain text with a trailing
 * chevron that slides right on hover/focus (design-system.md §12: motion that acknowledges
 * intent, nothing more). `TextButton` is a `<button>` for in-place actions (expand, show
 * more); `TextAnchor` is the plain `<a>` twin and is router-free by design — apps/web wraps
 * it in TanStack's `createLink` as `TextLink` (apps/web/src/components/ui/text-link.tsx).
 * Both respect `prefers-reduced-motion`.
 */
import { ChevronRight } from './icons.ts';
import { forwardRef, type AnchorHTMLAttributes, type ButtonHTMLAttributes } from 'react';

function textLinkClass(extra?: string): string {
  return ['argus-textlink', extra ?? ''].filter(Boolean).join(' ');
}

/** "View all ›" as a plain anchor (`href`) — for an app without a router, or an external link. */
export const TextAnchor = forwardRef<HTMLAnchorElement, AnchorHTMLAttributes<HTMLAnchorElement>>(
  function TextAnchor({ className, children, ...rest }, ref) {
    return (
      <a ref={ref} className={textLinkClass(className)} {...rest}>
        <span>{children}</span>
        <ChevronRight className="argus-textlink__chevron" size={14} aria-hidden="true" />
      </a>
    );
  },
);

/** "Show more ›" as a button (in-place action, not navigation). */
export const TextButton = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement>>(
  function TextButton({ className, children, type, ...rest }, ref) {
    return (
      <button ref={ref} type={type ?? 'button'} className={textLinkClass(className)} {...rest}>
        <span>{children}</span>
        <ChevronRight className="argus-textlink__chevron" size={14} aria-hidden="true" />
      </button>
    );
  },
);
